import React from "react";
import { GetData } from "../../../api/service";
import { ConfigProvider, Tag, Empty, Skeleton, Tooltip, Space } from "antd";
import Button from "@material-ui/core/Button";
import PieChart from "react-minimal-pie-chart";
import DashboardIcon from "@material-ui/icons/Dashboard";
import RefreshIcon from "@material-ui/icons/Refresh";
import DescriptionIcon from "@material-ui/icons/Description";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CancelIcon from "@material-ui/icons/Cancel";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import LocalLibraryIcon from "@material-ui/icons/LocalLibrary";
import PaymentIcon from "@material-ui/icons/Payment";
import PieChartIcon from "@material-ui/icons/PieChart";
import BarChartIcon from "@material-ui/icons/BarChart";
import HistoryIcon from "@material-ui/icons/History";
import CategoryIcon from "@material-ui/icons/Category";
import "../Verification/certificate-verification.css";

const COLORS = {
  blue: "#2f7bf5",
  green: "#16a34a",
  amber: "#f59e0b",
  red: "#dc2626",
  purple: "#7c4dff",
  teal: "#0ea5a4",
};

function StatTile({ icon, value, label, accent, soft, loading }) {
  return (
    <div className="cert-stat" style={{ "--accent": accent, "--soft": soft }}>
      <div
        className="cert-stat-icon"
        style={{ "--accent": accent, "--soft": soft }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="cert-stat-value">{loading ? "—" : value}</div>
        <div className="cert-stat-label">{label}</div>
      </div>
    </div>
  );
}

function QueueRow({ icon, label, count, accent }) {
  return (
    <div className="cert-queue">
      <span className="cert-queue-label">
        <span style={{ color: accent, display: "flex" }}>{icon}</span>
        {label}
      </span>
      <span className="cert-queue-count" style={{ color: accent }}>
        {count}
      </span>
    </div>
  );
}

export default function CertificateDashBoard(props) {
  const [requests, setRequests] = React.useState([]);
  const [types, setTypes] = React.useState([]);
  const [isLoaded, setIsLoaded] = React.useState(false);

  function loadData() {
    setIsLoaded(false);
    Promise.all([
      GetData(`/getcertificaterequests`),
      GetData(`/getcertificatetype`),
    ])
      .then(([reqs, tps]) => {
        setRequests(Array.isArray(reqs) ? reqs : []);
        setTypes(Array.isArray(tps) ? tps : []);
        setIsLoaded(true);
      })
      .catch(() => setIsLoaded(true));
  }

  React.useEffect(() => {
    loadData();
  }, []);

  const stats = React.useMemo(() => {
    let generated = 0;
    let pending = 0;
    let cancelled = 0;
    let paidAmount = 0;
    let paidCount = 0;
    let unpaid = 0;
    let verificationPending = 0;
    let libraryPending = 0;
    let byType = {};

    requests.forEach((el) => {
      if (el.status == 1) {
        generated++;
      } else if (el.status == 0) {
        pending++;
      } else {
        cancelled++;
      }

      if (el.payment_status == 1) {
        paidCount++;
        paidAmount += Number(el.amount_paid) || 0;
      } else {
        unpaid++;
      }

      if (el.status == 0 && el.verification_status == 0) {
        verificationPending++;
      }
      if (el.status == 0 && el.library_status == 0) {
        libraryPending++;
      }

      let key = el.type_name || "Others";
      byType[key] = (byType[key] || 0) + 1;
    });

    let typeRows = Object.keys(byType)
      .map((k) => ({ name: k, count: byType[k] }))
      .sort((a, b) => b.count - a.count);

    return {
      total: requests.length,
      generated,
      pending,
      cancelled,
      paidAmount,
      paidCount,
      unpaid,
      verificationPending,
      libraryPending,
      typeRows,
    };
  }, [requests]);

  const recent = React.useMemo(() => {
    return requests
      .slice()
      .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
      .slice(0, 6);
  }, [requests]);

  const pieData = [
    { title: "Generated", value: stats.generated, color: COLORS.green },
    { title: "In Process", value: stats.pending, color: COLORS.amber },
    { title: "Cancelled", value: stats.cancelled, color: COLORS.red },
  ].filter((el) => el.value > 0);

  const typePalette = [
    COLORS.blue,
    COLORS.purple,
    COLORS.teal,
    COLORS.amber,
    COLORS.green,
    COLORS.red,
  ];

  return (
    <ConfigProvider
      theme={{ token: { colorPrimary: COLORS.blue, borderRadius: 8 } }}
    >
      <div className="cert-page" style={{ padding: "14px 8px 24px" }}>
        <div className="cert-hero">
          <div className="cert-hero-inner">
            <div className="cert-hero-icon">
              <DashboardIcon style={{ fontSize: 26 }} />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h2 className="cert-hero-title">Certificate Dashboard</h2>
              <p className="cert-hero-sub">
                Live snapshot of certificate requests, approvals and collection.
                Certificate types must be configured before generation.
              </p>
            </div>
            <Tooltip title="Reload statistics">
              <Button
                size="small"
                onClick={loadData}
                style={{
                  minWidth: 38,
                  borderRadius: 8,
                  background: "#eaf2ff",
                  color: COLORS.blue,
                }}
              >
                <RefreshIcon style={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="cert-stats">
          <StatTile
            loading={!isLoaded}
            icon={<DescriptionIcon style={{ fontSize: 22 }} />}
            value={stats.total}
            label="Total Requests"
            accent={COLORS.blue}
            soft="#eaf2ff"
          />
          <StatTile
            loading={!isLoaded}
            icon={<DoneAllIcon style={{ fontSize: 22 }} />}
            value={stats.generated}
            label="Certificates Generated"
            accent={COLORS.green}
            soft="#e7f8ec"
          />
          <StatTile
            loading={!isLoaded}
            icon={<HourglassEmptyIcon style={{ fontSize: 22 }} />}
            value={stats.pending}
            label="In Process"
            accent={COLORS.amber}
            soft="#fff5e0"
          />
          <StatTile
            loading={!isLoaded}
            icon={<CancelIcon style={{ fontSize: 22 }} />}
            value={stats.cancelled}
            label="Cancelled"
            accent={COLORS.red}
            soft="#fdeaea"
          />
        </div>

        <div className="cert-dash-grid">
          <div className="cert-panel">
            <div className="cert-panel-title">
              <PieChartIcon style={{ fontSize: 18, color: COLORS.blue }} />
              Request Status Split
            </div>

            {!isLoaded ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : pieData.length === 0 ? (
              <Empty description="No requests yet." />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 22,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ width: 150, height: 150 }}>
                  <PieChart
                    data={pieData}
                    lineWidth={26}
                    rounded
                    animate
                    startAngle={-90}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  {pieData.map((el, index) => (
                    <div key={index} className="cert-bar-row">
                      <div className="cert-bar-head">
                        <span>{el.title}</span>
                        <b>
                          {el.value} (
                          {stats.total > 0
                            ? Math.round((el.value / stats.total) * 100)
                            : 0}
                          %)
                        </b>
                      </div>
                      <div className="cert-bar-track">
                        <div
                          className="cert-bar-fill"
                          style={{
                            "--accent": el.color,
                            width: `${
                              stats.total > 0
                                ? (el.value / stats.total) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="cert-panel">
            <div className="cert-panel-title">
              <HourglassEmptyIcon style={{ fontSize: 18, color: COLORS.amber }} />
              Pending Queues
            </div>

            {!isLoaded ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <>
                <QueueRow
                  icon={<VerifiedUserIcon style={{ fontSize: 18 }} />}
                  label="Awaiting verification"
                  count={stats.verificationPending}
                  accent={COLORS.blue}
                />
                <QueueRow
                  icon={<LocalLibraryIcon style={{ fontSize: 18 }} />}
                  label="Awaiting library clearance"
                  count={stats.libraryPending}
                  accent={COLORS.purple}
                />
                <QueueRow
                  icon={<PaymentIcon style={{ fontSize: 18 }} />}
                  label="Payment not received"
                  count={stats.unpaid}
                  accent={COLORS.red}
                />
                <QueueRow
                  icon={<CategoryIcon style={{ fontSize: 18 }} />}
                  label="Certificate types configured"
                  count={types.length}
                  accent={COLORS.teal}
                />
              </>
            )}
          </div>

          <div className="cert-panel">
            <div className="cert-panel-title">
              <AccountBalanceWalletIcon
                style={{ fontSize: 18, color: COLORS.green }}
              />
              Fee Collection
            </div>

            {!isLoaded ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <>
                <div className="cert-money">
                  Rs. {stats.paidAmount.toLocaleString("en-IN")}
                </div>
                <div className="cert-muted" style={{ marginTop: 4 }}>
                  Collected from {stats.paidCount} paid request
                  {stats.paidCount === 1 ? "" : "s"}
                </div>

                <div className="cert-bar-row" style={{ marginTop: 18 }}>
                  <div className="cert-bar-head">
                    <span>Paid</span>
                    <b>
                      {stats.paidCount} of {stats.total}
                    </b>
                  </div>
                  <div className="cert-bar-track">
                    <div
                      className="cert-bar-fill"
                      style={{
                        "--accent": COLORS.green,
                        width: `${
                          stats.total > 0
                            ? (stats.paidCount / stats.total) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>

                <Space wrap style={{ marginTop: 6 }}>
                  <Tag color="green">Paid: {stats.paidCount}</Tag>
                  <Tag color="red">Unpaid: {stats.unpaid}</Tag>
                </Space>
              </>
            )}
          </div>

          <div className="cert-panel">
            <div className="cert-panel-title">
              <BarChartIcon style={{ fontSize: 18, color: COLORS.purple }} />
              Requests by Certificate Type
            </div>

            {!isLoaded ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : stats.typeRows.length === 0 ? (
              <Empty description="No requests yet." />
            ) : (
              stats.typeRows.slice(0, 6).map((el, index) => (
                <div key={index} className="cert-bar-row">
                  <div className="cert-bar-head">
                    <span>{el.name}</span>
                    <b>{el.count}</b>
                  </div>
                  <div className="cert-bar-track">
                    <div
                      className="cert-bar-fill"
                      style={{
                        "--accent": typePalette[index % typePalette.length],
                        width: `${
                          stats.total > 0 ? (el.count / stats.total) * 100 : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="cert-panel" style={{ gridColumn: "1 / -1" }}>
            <div className="cert-panel-title">
              <HistoryIcon style={{ fontSize: 18, color: COLORS.teal }} />
              Latest Requests
            </div>

            {!isLoaded ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : recent.length === 0 ? (
              <Empty description="No requests yet." />
            ) : (
              recent.map((el, index) => (
                <div key={index} className="cert-recent-row">
                  <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                    <div className="cert-name">{el.name}</div>
                    <div className="cert-muted">
                      {el.type_name} &middot; Roll {el.roll_no || "—"}
                    </div>
                  </div>
                  <div style={{ flex: "0 1 160px" }} className="cert-muted">
                    {el.time_stamp}
                  </div>
                  <div style={{ flex: "0 1 130px" }} className="cert-muted">
                    At: <b style={{ color: "#33415c" }}>{el.now_at || "—"}</b>
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    {el.payment_status == 1 ? (
                      <Tag color="green">Paid</Tag>
                    ) : (
                      <Tag color="red">Unpaid</Tag>
                    )}
                    {el.status == 1 ? (
                      <Tag color="green">Generated</Tag>
                    ) : el.status == 0 ? (
                      <Tag color="gold">In Process</Tag>
                    ) : (
                      <Tag color="red">Cancelled</Tag>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
